<template>
	<div
		:class="[
			'notification-block',
			disabled && 'is-disabled',
			...stateClassList
		]"
		:disabled="disabled"
		data-testid="notification-block"
		v-if="isVisibleOnFeatureSwitch"
		v-show="isVisible"
	>
		<div class="notification-block__body">
			<div
				class="notification-block__body-icon"
				v-if="iconClassName"
				data-testid="notification-block-icon"
			>
				<i class="icon" :class="iconClassName"></i>
			</div>

			<div class="notification-block__body-group">
				<span
					class="notification-block__body-title"
					data-testid="notification-block-header"
					v-if="title"
				>
					{{ title }}
				</span>
				<div
					class="notification-block__body-text"
					data-testid="notification-block-body"
				>
					<slot />
				</div>
			</div>

			<div
				class="notification-block__body-action"
				v-if="$slots['btnSlot'] != undefined"
			>
				<slot name="btnSlot" />
			</div>
			
			<button
				class="notification-block__close-btn"
				v-if="closeBtn"
				@click="onClose"
				ref="closeButton"
			>
				<i class="icon icon-remove"></i>
			</button>
		</div>
	</div>
</template>

<script>
import baseComponentMixin from '@/mixins/base-component-mixin';
import componentStateMixin from '@/mixins/component-state-mixin';

export default {
	name: 'NotificationBlock',
	props: {
		title: {
			type: String,
			default: ''
		},
		iconClassName: {
			type: String,
			default: ''
		},
		closeBtn: {
			type: Boolean,
			default: false
		}
	},
	mixins: [baseComponentMixin, componentStateMixin],
	data() {
		return {
			isVisible: true
		};
	},
	methods: {
		onClose() {
			this.isVisible = false;
			this.$emit('close');
		}
	}
};
</script>

<style lang="scss" scoped>
/*------------------------------------*\
  #BASE-COMPONENT-STYLES
  The base class and variant classes, if applicable, are never added/removed via dev logic.
\*------------------------------------*/
.notification-block {
	display: grid;
	grid-template-columns: 1fr auto;
	padding: $padding-md;
	border: $notification-border-width $notification-border-style
		$notification-border-color;
	border-radius: $border-radius;
	background: $notification-bg;
	color: $notification-fg;

	&__body {
		display: flex;
		flex-direction: row;
	}

	&__body-icon {
		font-size: 16px;
		color: $notification-fg;
		margin-right: $gutter-md;
	}

	&__body-group {
		display: flex;
		flex-direction: column;
		gap: $gutter-md;
		font-size: $txt-font-size;
		width: 100%;
	}

	&__body-title {
		@include heading(sm);
	}

	&__body-action {
		display: flex;
		align-items: center;
		margin-left: $gutter-xl;
	}

	&__close-btn {
		@include btn-icon-clear;
		position: relative;
		height: 12px;
		font-size: 12px;
		text-align: right;
		margin-left: $gutter-sm;
		color: $ui-fg-dark;
		//right: $gutter-xl;

		&:hover,
		&:focus,
		&:active {
			color: var(--brand-2);

			.icon:after {
				position: absolute;
				width: 12px;
				content: '';
				bottom: -$gutter-sm;
				right: 0;
				border-bottom: 1px solid var(--brand-2);
			}
		}
	}
}

/*------------------------------------*\
  #STATE-TYPE-STYLES
  This section is only for notification-block CSS change via state type.
  Neutral state is the default, CSS for that is located in the base component styles section.
  These classes are added/removed as needed via dev logic
\*------------------------------------*/

.notification-block {
	&.is-info {
		border-color: var(--ui-neutral);
		background: var(--ui-neutral-light);
	}

	&.is-warning {
		border-color: var(--ui-warning);
		background: var(--ui-warning-light);
	}

	&.is-negative {
		border-color: var(--ui-negative);
		background: var(--ui-negative-light);
		color: $notification-fg;
	}

	&.is-positive {
		border-color: var(--ui-positive);
		background: var(--ui-positive-light);
	}
}
</style>
